import { useMutation } from "@apollo/client/react";
import {
  Button,
  ButtonSpinner,
  ButtonText,
} from "@repo/components/src/ui/button";
import { RequireMagicLinkDocument } from "@repo/graphql/documents/user/mutation.generated";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { showToast } from "../toast";

/** The backend sends at most one login link every five minutes. */
export const RESEND_COOLDOWN_MS = 5 * 60 * 1000;

type Props = {
  email: string;
  /** When the backend sends another link (epoch ms). */
  resendAt: number;
  onSent: () => void;
};

/**
 * «Reenviar enlace»: asks for another login link to `email`, counting down to
 * `resendAt`. The countdown is read off the clock on every tick, not counted, so it is
 * right after the tab or app was in the background; the parent owns `resendAt`, so
 * opening and cancelling «Cambiarlo» keeps it.
 */
const ResendLinkButton = ({ email, resendAt, onSent }: Props) => {
  const [now, setNow] = useState(Date.now);
  // Capped: right after a resend `now` is up to a tick old.
  const remaining = Math.min(
    RESEND_COOLDOWN_MS / 1000,
    Math.max(0, Math.ceil((resendAt - now) / 1000)),
  );
  const [requireMagicLink, { loading }] = useMutation(
    RequireMagicLinkDocument,
    {
      onCompleted: () => {
        onSent();
        showToast("success", "Te enviamos un nuevo enlace.");
      },
      onError: () =>
        showToast(
          "error",
          "No pudimos reenviar el enlace. Inténtalo de nuevo.",
        ),
    },
  );

  useEffect(() => {
    if (resendAt <= Date.now()) return;
    const timer = setInterval(() => {
      const tick = Date.now();
      setNow(tick);
      if (resendAt <= tick) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendAt]);

  const waiting = remaining > 0;

  return (
    <Button
      action="brand"
      variant="outline"
      rounded="lg"
      className="w-full"
      isDisabled={waiting || loading}
      onPress={() => void requireMagicLink({ variables: { email } })}
    >
      {loading ? <ButtonSpinner /> : null}
      <ButtonText>
        {waiting
          ? `Reenviar enlace en ${dayjs.duration(remaining, "seconds").format("m:ss")}`
          : "Reenviar enlace"}
      </ButtonText>
    </Button>
  );
};

export default ResendLinkButton;
